import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import { Magnetic } from "./Magnetic";

export const Nav = () => {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#about", label: t.nav.about, type: "anchor" },
    { href: "#protocol", label: t.nav.protocol, type: "anchor" },
    { href: "#services", label: t.nav.services, type: "anchor" },
    { href: "/concierge", label: t.nav.concierge, type: "route" },
    { href: "#portfolio", label: t.nav.portfolio, type: "anchor" },
    { href: "/blog", label: t.nav.blog || "Blog", type: "route" },
    { href: "#contact", label: t.nav.contact, type: "anchor" },
  ];

  const handleNav = (l) => {
    setOpen(false);
    if (l.type === "route") {
      navigate(l.href);
      return;
    }
    if (!isHome) {
      navigate("/" + l.href);
      return;
    }
    const el = document.querySelector(l.href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goContact = () => {
    setOpen(false);
    if (!isHome) {
      navigate("/#contact");
    } else {
      const el = document.querySelector("#contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Adaptive glassmorphism nav:
  // - At the very top of Home hero → transparent smoky glass + light text + white logo
  // - Anywhere else / any other page → solid ivory glass + dark text + dark logo
  const heroPass = isHome ? scrolled : true;

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.5, duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        heroPass
          ? "bg-[#F1EEE8]/[0.94] backdrop-blur-xl border-b border-[#232326]/10 text-[#232326]"
          : "bg-transparent backdrop-blur-0 border-b border-transparent text-[#F1EEE8]"
      }`}
      data-testid="main-nav"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center"
          data-testid="brand-logo"
          aria-label="Zitro Property Management"
        >
          <img
            src={heroPass ? "/brand/zitro-logo-dark.png" : "/brand/zitro-logo-white.png"}
            alt="Zitro Property Management"
            className="h-14 md:h-16 lg:h-[68px] w-auto object-contain select-none transition-opacity duration-500"
            draggable="false"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => handleNav(l)}
              className={`link-underline text-[0.7rem] tracking-[0.22em] uppercase font-medium transition-colors ${
                heroPass ? "text-[#232326] hover:text-[#8A6F45]" : "text-[#F1EEE8] hover:text-[#DDD4B9]"
              }`}
              data-testid={`nav-${l.href.replace(/[#/]/g, "")}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase font-medium" data-testid="lang-switcher">
            {["it","en","de","fr","ru"].map((code, i) => (
              <span key={code} className="flex items-center gap-2">
                {i > 0 && <span className="opacity-35">/</span>}
                <button
                  onClick={() => setLang(code)}
                  className={`transition-opacity ${lang === code ? "opacity-100" : "opacity-45 hover:opacity-80"}`}
                  data-testid={`lang-${code}`}
                >
                  {code.toUpperCase()}
                </button>
              </span>
            ))}
          </div>

          <Magnetic strength={0.22} className="hidden md:inline-block">
            <button
              onClick={goContact}
              className={heroPass ? "btn-primary" : "btn-primary-inverse"}
              data-testid="nav-cta"
            >
              {t.nav.cta}
            </button>
          </Magnetic>

          <button
            className="lg:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X size={22} weight="light" /> : <List size={22} weight="light" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden bg-[#F1EEE8] text-[#232326] border-t border-[#232326]/10"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => handleNav(l)}
                  className="font-display text-3xl tracking-tight text-left"
                >
                  {l.label}
                </button>
              ))}
              <div className="flex items-center gap-3 pt-2 text-xs tracking-[0.22em] uppercase flex-wrap">
                {["it","en","de","fr","ru"].map((code, i) => (
                  <span key={code} className="flex items-center gap-3">
                    {i > 0 && <span className="opacity-30">/</span>}
                    <button onClick={() => setLang(code)} className={lang === code ? "" : "opacity-40"}>{code.toUpperCase()}</button>
                  </span>
                ))}
              </div>
              <button
                onClick={goContact}
                className="btn-primary self-start"
              >
                {t.nav.cta}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
